import { DateTime } from 'luxon';
import type { FilenameParts, ParsedFilenameMetadata } from '@/lib/types/Upload';
import { VIDEO_CONSTRAINTS, REF_ID_PATTERN } from '@/lib/constants/media';

export class FilenameParser {
  /**
   * Split filename into basename + extension.
   * Reference: lib2/util/file/FilenameParts.ts
   */
  static parse(filename: string): FilenameParts {
    const trimmed = filename.trim();
    if (trimmed.length === 0) {
      throw new Error(`Error parsing filename parts from filename: ${filename}`);
    }

    const extDotIndex = trimmed.lastIndexOf('.');
    if (extDotIndex === -1) {
      return { filename: trimmed, basename: trimmed, ext: null };
    }

    const basename = trimmed.substring(0, extDotIndex);
    const ext = extDotIndex + 1 === trimmed.length
      ? null
      : trimmed.substring(extDotIndex + 1);

    return { filename: trimmed, basename, ext };
  }

  /**
   * Extract metadata using two-parser strategy with priority fallback.
   * Reference: lib2/model/media/CreateMediaReq.ts -> createVideoReqFromFilename()
   *
   * The published date returned here feeds into the priority chain in
   * buildCreateVideoRequest(), where it OVERRIDES mediaSettings.published.
   */
  static parseMetadata(filename: string, lastModified?: number): ParsedFilenameMetadata {
    const parts = this.parse(filename);
    const tokenResult = this.parsePlatformTokens(parts.basename);

    if (tokenResult.hasTokens) {
      // Guard against empty title (e.g., filename = "refidabc123.mp4")
      if (!tokenResult.metadata.title) {
        tokenResult.metadata.title = parts.basename;
      }
      return tokenResult.metadata;
    }

    return this.parseSH1(parts.basename, lastModified);
  }

  /**
   * PlatformIngestTokenParser — extracts refid<value> tokens from underscore-delimited filenames.
   * Reference: lib2/model/ingest/PlatformIngestTokenParser.ts
   *
   * Splits basename on `_`, extracts `refid<value>` tokens via regex.
   * Non-token parts concatenated as title with `_` separator (preserves underscores).
   * Triggers if refId was found (hasOneOrMoreTokens).
   * NOTE: Legacy had date extraction commented out — intentionally NOT reimplemented.
   */
  private static parsePlatformTokens(
    basename: string
  ): { hasTokens: boolean; metadata: ParsedFilenameMetadata } {
    const RefIdRegex = /^refid(.+?)$/;
    const parts = basename.split('_');

    let refId: string | null = null;
    let title = '';

    for (const p of parts) {
      const refIdMatch = RefIdRegex.exec(p);
      if (refIdMatch && refIdMatch.length === 2) {
        refId = refIdMatch[1];
      } else {
        title += title === '' ? p : `_${p}`;
      }
    }

    const hasTokens = refId !== null;

    // Validate refId format and length
    if (refId) {
      if (!REF_ID_PATTERN.test(refId) || refId.length > VIDEO_CONSTRAINTS.REF_ID_MAX) {
        console.log(`Ignoring invalid refId from filename: ${refId}`);
        refId = null;
      }
    }

    return {
      hasTokens,
      metadata: { title, refId, published: null },
    };
  }

  /**
   * SH1FilenameParser — extracts YYYYMMDD dates, filters technical metadata, title-cases remainder.
   * Reference: lib2/util/sh1/SH1FilenameParser.ts
   *
   * Splits basename on `_`, detects and filters:
   *   YYYYMMDD -> published date
   *   NNNkbps -> ignore
   *   NNNs -> ignore
   *   WxH -> ignore
   * Remaining parts -> title (title-cased with spaces).
   *
   * IMPORTANT (matches legacy): SH1's cleaned/title-cased title is ONLY used when
   * SH1 found a date. If no date is found, return the raw basename as title.
   * This matches CreateMediaReq.ts:157-162.
   */
  private static parseSH1(
    basename: string,
    lastModified?: number
  ): ParsedFilenameMetadata {
    const DurationRegex = /^(\d+)s$/;
    const KbpsRegex = /^(\d+)kbps$/;
    const SizeRegex = /^(\d+)x(\d+)$/;

    const parts = basename.split('_');
    let publishedDate: number | null = null;
    let title = '';

    for (const p of parts) {
      const durationMatch = DurationRegex.exec(p);
      const kbpsMatch = KbpsRegex.exec(p);
      const sizeMatch = SizeRegex.exec(p);
      const mDate = this.getMomentFromIntDay(p);

      if (durationMatch && durationMatch.length === 2) {
        // Ignore duration metadata
      } else if (kbpsMatch && kbpsMatch.length === 2) {
        // Ignore bitrate metadata
      } else if (sizeMatch && sizeMatch.length === 3) {
        // Ignore resolution metadata
      } else if (mDate !== null) {
        publishedDate = mDate;
      } else {
        title += title === '' ? this.toTitleCase(p) : ` ${this.toTitleCase(p)}`;
      }
    }

    // Validate date within +-100 years
    if (publishedDate !== null) {
      const nowSecs = Date.now() / 1000;
      const oneYearSecs = 31536000;
      const minConstraint = nowSecs - oneYearSecs * 100;
      const maxConstraint = nowSecs + oneYearSecs * 100;
      if (publishedDate < minConstraint || publishedDate > maxConstraint) {
        console.log(`Ignoring provided published timestamp: ${publishedDate}. Value is out of range.`);
        publishedDate = null;
      }
    }

    // Legacy behavior: title-cased title is only used when a date was found
    if (publishedDate !== null) {
      return { title, refId: null, published: publishedDate };
    }

    return { title: basename, refId: null, published: null };
  }

  /**
   * Parse YYYYMMDD string into epoch seconds.
   * Uses Luxon instead of moment.js.
   */
  private static getMomentFromIntDay(intDayStr: string): number | null {
    const IntDayRegex = /^(\d{4})(\d{2})(\d{2})$/;
    const m = IntDayRegex.exec(intDayStr);
    if (m && m.length === 4) {
      const month = parseInt(m[2], 10);
      const day = parseInt(m[3], 10);
      if (month > 0 && month < 13 && day > 0 && day < 32) {
        const dt = DateTime.fromFormat(intDayStr, 'yyyyMMdd');
        if (dt.isValid) {
          return Math.floor(dt.toSeconds());
        }
      }
    }
    return null;
  }

  /**
   * Title-case regex matching legacy SH1FilenameParser.ts behavior.
   * Capitalizes every word including articles/prepositions.
   */
  private static toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, (txt) =>
      txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    );
  }
}
